import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaLocationArrow } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import IconBadge from '../../icons/IconBadge';
import { SelectOption } from '../../../../types/misc';
import CancelSaveButton from '../../misc/CancelSaveButton';
import { isValidLocalContact } from '../../../../utils/validationUtils';
import localContactService from '../../../../services/community/localContactService';
import { LocalContactInfo } from '../../../../models/localContactService.model';
import CountryDropdown from '../../forms/CountryDropdown';
import { ProfileField } from '../../../../types/Profile';
import { CommunityInvite } from '../../../../models/inviteService.model';

export function ChooseCountryHeader(): JSX.Element {
  const { t } = useTranslation('', {
    keyPrefix: 'component.modal.importLocalContacts.chooseCountry',
  });

  return (
    <div className="mb-2 flex items-center">
      <IconBadge icon={FaLocationArrow} />

      <div className="ml-4 flex w-full flex-col">
        <h1 className="text-3xl font-semibold text-primary-900">{t('title')}</h1>
        <p className="text-base text-gray-600">{t('subtitle')}</p>
      </div>
    </div>
  );
}

interface ChooseCountryViewProps {
  invite: CommunityInvite;
  contactsWithMissingCountry: LocalContactInfo[];
  setLocalContacts: (contacts: LocalContactInfo[]) => void;
  onSubmitSuccessful?: () => void;
  onMoreDataNeeded?: () => void;
  onSubmitFailed?: () => void;
}

export function ChooseCountryView({
  invite,
  contactsWithMissingCountry,
  setLocalContacts,
  onSubmitSuccessful,
  onMoreDataNeeded,
  onSubmitFailed,
}: ChooseCountryViewProps): JSX.Element {
  const { t } = useTranslation();
  const [country, setCountry] = useState<SelectOption>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!country) {
      toast.error(t('toast.error.missingCountry'));
      return;
    }

    setIsSubmitting(true);
    try {
      const withCountry = contactsWithMissingCountry.map((c) => ({
        ...c,
        receivedData: c.receivedData.map(
          (r): ProfileField => ({
            ...r,
            address: r.address
              ? { ...r.address, country: country.label, countryCode: country.value }
              : undefined,
          }),
        ),
      }));

      if (withCountry.every((c) => isValidLocalContact(c, invite))) {
        await localContactService.createLocalContacts(withCountry);
        toast.success(t('toast.success.importLocalContacts'));
        onSubmitSuccessful?.();
      } else {
        setLocalContacts(withCountry);
        onMoreDataNeeded?.();
      }
    } catch (error) {
      toast.error(t('toast.error.importLocalContacts'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="mb-8">
        <ChooseCountryHeader />
      </div>
      <div className="flex w-full flex-col gap-6">
      <div className="w-full rounded-2xl border bg-secondary-200/50 p-6">
        <div className="flex flex-col gap-3">
          <label className="text-base font-semibold text-primary-900">{t('dataType.country')}</label>
          <CountryDropdown
            netherlandsFirst
            selectedCountry={country}
            setSelectedCountry={setCountry}
          />
          <p className="text-sm text-gray-600">
            {t('component.modal.importLocalContacts.chooseCountry.instruction', { defaultValue: 'Select a country to apply to all addresses' })}
          </p>
        </div>
      </div>

      <div className="flex w-full justify-end border-t border-primary-200 pt-6">
        <CancelSaveButton 
          onCancel={() => onSubmitFailed?.()} 
          onSave={handleSubmit}
          loading={isSubmitting}
        />
      </div>
    </div>
    </>
  );
}
